import { Navigate, Route, Routes } from 'react-router';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import {
  useSessao, podeGerenciarCatalogo, podeCadastroRapido, podeVerCaixa, podeGerenciarEquipe,
  podeGerenciarVendas,
  podeVerFaturamento, podeVerFinancas, podeAdministrar,
  podeLancarCompraNaHora,
  useLiberacaoMeuDinheiro,
} from './lib/sessao';
import { configurado } from './lib/supabase';
import Layout from './componentes/Layout';
import Carregando from './componentes/Carregando';
import Login from './paginas/Login';
import Busca from './paginas/Busca';
import Peca from './paginas/Peca';
import Cadastro from './paginas/Cadastro';
import CadastroDoBalcao from './paginas/CadastroDoBalcao';
import Conferencia from './paginas/Conferencia';
import Faxina from './paginas/Faxina';
import Importar from './paginas/Importar';
import Venda from './paginas/Venda';
import Impressoes from './paginas/Impressoes';
import Pedidos from './paginas/Pedidos';
import Vendas from './paginas/Vendas';
import Orcamento from './paginas/Orcamento';
import Orcamentos from './paginas/Orcamentos';
import Metas from './paginas/Metas';
import AVer from './paginas/AVer';
import Entrada from './paginas/Entrada';
import Caixa from './paginas/Caixa';
import Relatorios from './paginas/Relatorios';
import Painel from './paginas/Painel';
import Equipe from './paginas/Equipe';
import Caracteristicas from './paginas/Caracteristicas';
import Estoque from './paginas/Estoque';
import Promocoes from './paginas/Promocoes';
import Encomendas from './paginas/Encomendas';
import Agenda from './paginas/Agenda';
import Notas from './paginas/Notas';
import ContasPagar from './paginas/ContasPagar';
import Fluxo from './paginas/Fluxo';
import Parceiros from './paginas/Parceiros';
import Compras from './paginas/Compras';
import Fiado from './paginas/Fiado';
import Clientes from './paginas/Clientes';
import Fornecedores from './paginas/Fornecedores';
import CompraNaHora from './paginas/CompraNaHora';
import Sistema from './paginas/Sistema';
import SemConfiguracao from './paginas/SemConfiguracao';

// Meu dinheiro (ADR-008) em pacote à parte: é a tela de UMA pessoa, que o
// balcão não usa — quem nunca abre não baixa.
const MeuDinheiro = lazy(() => import('./pessoal/paginas/MeuDinheiro'));
// O mapa 3D também: é desenho pesado para uma consulta de vez em quando.
const MapaDaLoja = lazy(() => import('./paginas/MapaDaLoja'));

// A tela de provisões pelo mesmo motivo do mapa: ela carrega os gráficos e o
// motor de simulação, que o balcão nunca abre — é conta de dono, uma vez por
// mês, e não tem por que pesar no bundle de quem está vendendo peça.
const Provisoes = lazy(() => import('./paginas/Provisoes'));

/**
 * A CONSULTA do perfil falhou — rede caída ou sessão vencida (o caso real:
 * computador voltando da suspensão com o token velho). É diferente de "sem
 * perfil": aqui a orientação é reconectar, não pedir acesso ao dono.
 */
function PerfilIndisponivel() {
  const { sair, recarregarPerfil } = useSessao();
  const [tentando, setTentando] = useState(false);

  const tentarDeNovo = async () => {
    setTentando(true);
    try {
      await recarregarPerfil();
    } finally {
      setTentando(false);
    }
  };

  return (
    <Layout titulo="Sem conexão">
      <div className="cartao p-6">
        <h2 className="text-lg font-bold text-alerta">Não foi possível carregar seu perfil</h2>
        <p className="mt-2 text-sm text-mute">
          Seu login está certo, mas a consulta ao servidor falhou — normalmente é internet
          instável ou sessão vencida depois de o aparelho ficar um tempo suspenso. Verifique a
          conexão e tente de novo; se não resolver, saia e entre novamente.
        </p>
        <div className="mt-4 flex gap-3">
          <button type="button" className="botao-primario" disabled={tentando}
            onClick={() => void tentarDeNovo()}>
            {tentando ? 'Tentando…' : 'Tentar de novo'}
          </button>
          <button type="button" className="botao-secundario" onClick={() => void sair()}>
            Sair
          </button>
        </div>
      </div>
    </Layout>
  );
}

/**
 * Trava de rota. É conveniência de UI: quem decide de verdade é a RLS no
 * Postgres. Toda tela protegida aqui também é protegida lá.
 */
function Protegida({ permitido, children }: { permitido: boolean; children: ReactNode }) {
  const { session, perfil, perfilErro, carregando } = useSessao();

  if (carregando) return <Carregando texto="Carregando sessão" />;
  if (!session) return <Navigate to="/login" replace />;

  // Sem resposta do banco não dá para afirmar que o perfil não existe — cai na
  // tela de reconexão, não na de "peça ao dono" (diagnóstico de 2026-08-05).
  if (!perfil && perfilErro) return <PerfilIndisponivel />;

  // Autenticado no Supabase mas sem linha em profiles (a consulta FUNCIONOU e
  // veio vazia): o banco vai negar tudo.
  // Melhor dizer isso na cara do que deixar a pessoa achar que o app quebrou.
  if (!perfil || !perfil.ativo) {
    return (
      <Layout titulo="Sem acesso">
        <div className="cartao p-6">
          <h2 className="text-lg font-bold text-alerta">Perfil não liberado</h2>
          <p className="mt-2 text-sm text-mute">
            Seu login existe, mas ainda não há um perfil ativo vinculado a ele. Peça ao dono ou ao
            gerente para criar seu acesso na tela de Equipe.
          </p>
        </div>
      </Layout>
    );
  }

  if (!permitido) {
    return (
      <Layout titulo="Sem permissão">
        <div className="cartao p-6">
          <h2 className="text-lg font-bold text-alerta">Acesso restrito</h2>
          <p className="mt-2 text-sm text-mute">
            Seu papel ({perfil.papel}) não tem acesso a esta tela.
          </p>
        </div>
      </Layout>
    );
  }

  return <>{children}</>;
}

/**
 * A rota do Meu dinheiro. Liberado é decisão do dono, por papel (migração 079),
 * e chega numa consulta: enquanto ela não volta, "Acesso restrito" seria
 * mentira. Quem nega de verdade é o banco pessoal, que só atende a lista
 * liberada — isto só evita abrir uma tela que ele vai recusar.
 */
function RotaDoMeuDinheiro() {
  const { liberado, carregando } = useLiberacaoMeuDinheiro();
  if (carregando) return <Carregando texto="Carregando" />;
  return (
    <Protegida permitido={liberado}>
      <Suspense fallback={<Carregando texto="Abrindo o Meu dinheiro" />}>
        <MeuDinheiro />
      </Suspense>
    </Protegida>
  );
}

export default function App() {
  const { papel } = useSessao();

  if (!configurado) return <SemConfiguracao />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/" element={<Protegida permitido><Busca /></Protegida>} />
      <Route path="/peca/:id" element={<Protegida permitido><Peca /></Protegida>} />
      <Route path="/venda" element={<Protegida permitido><Venda /></Protegida>} />
      <Route path="/orcar" element={<Protegida permitido><Venda modo="orcamento" /></Protegida>} />

      {/* Uma rota, dois escopos: a RLS decide se aparecem as vendas de todos
          ou só as de quem está logado (core/sql/policies/rls.sql §2). */}
      <Route path="/vendas" element={<Protegida permitido><Vendas /></Protegida>} />

      {/* Promoções é pauta de VENDA, não gestão: todo papel ativo consulta.
          Definir o preço promocional continua no cadastro (gerente/dono). */}
      <Route path="/promocoes" element={<Protegida permitido><Promocoes /></Protegida>} />

      {/* Clientes: telefone, CPF, carros e placa — a ficha de quem compra, que
          o gerente usa para cobrar e para oferecer a peça certa. Fica na régua
          da gestão de venda (28/09/2026, antes era a do caixa): o gerente não
          pode perder o telefone do cliente por causa do faturamento. O bloco
          "Compras" (quanto ele já gastou) é o que saiu, lá dentro.
          CADASTRAR é de todo mundo, e acontece na tela de Venda — quem libera
          o insert é a RLS §16, não esta rota. */}
      <Route path="/clientes" element={<Protegida permitido={podeGerenciarVendas(papel)}><Clientes /></Protegida>} />
      {/* O fechamento (052) — não confundir com /compras, que acompanha os
          pedidos já enviados. Esta tela é onde eles NASCEM.
          Aberta a TODOS desde 053: o vendedor entra para SUGERIR peça (e vê
          só as sugestões dele); o fechamento em si continua da gerência —
          quem recorta é a RLS de cada consulta, não a rota. */}
      <Route path="/pedidos" element={<Protegida permitido><Pedidos /></Protegida>} />
      {/* A fila de impressão: quem imprime é gerência, quem conserta também.
          Segue na régua da gestão de venda desde 28/09/2026 — era a do caixa,
          que virou só do dono, e reimprimir vale e recibo continua sendo
          trabalho do gerente. */}
      <Route path="/impressoes" element={<Protegida permitido={podeGerenciarVendas(papel)}><Impressoes /></Protegida>} />

      {/* Fornecedores: mesma régua da Entrada de mercadoria, porque é lá que o
          cadastro é usado — quem recebe a mercadoria é quem conhece o
          fornecedor. A RLS já decide igual (rls.sql §1). */}
      <Route
        path="/fornecedores"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Fornecedores /></Protegida>}
      />

      {/* Encomendas: todo papel ativo entra, mas a RLS (§14) decide O QUE cada
          um enxerga — vendedor vê as dele, gerente e dono veem todas. Uma rota
          só, dois escopos, como em /vendas. */}
      <Route path="/encomendas" element={<Protegida permitido><Encomendas /></Protegida>} />

      {/* Agenda (038): todo perfil ativo entra — o vendedor vê a rotina DELE e
          as tarefas da loja; a gerência troca de pessoa e monta a rotina dos
          que estão abaixo. Quem recorta é a RLS + a hierarquia da RPC. */}
      <Route path="/agenda" element={<Protegida permitido><Agenda /></Protegida>} />

      {/* Notas (072): o mural. TODO perfil ativo entra e vê o mesmo mural — é
          a única tela do sistema sem recorte por papel nem por pessoa, e isso é
          o pedido, não um esquecimento: recado que cada um vê diferente não
          serve para combinar nada. Quem pode BAIXAR a nota continua sendo
          decidido no banco, nota a nota. */}
      <Route path="/notas" element={<Protegida permitido><Notas /></Protegida>} />

      {/* Contas a pagar (039): financeiro é do DONO, como a folha. A RLS diz
          o mesmo — o gerente não enxerga uma linha sequer. */}
      <Route path="/contas" element={<Protegida permitido={podeVerFinancas(papel)}><ContasPagar /></Protegida>} />
      {/* A linha do tempo do dinheiro (23/08/2026). Só dono — lê contas a
          pagar e folha, que são dele. */}
      <Route path="/fluxo" element={<Protegida permitido={podeVerFinancas(papel)}><Fluxo /></Protegida>} />

      {/* Provisões (085): o simulador de custos, a DRE projetada e quanto
          guardar por mês. Só dono, a mesma régua de /contas e /fluxo — lê
          folha, custo e resultado. Vem por lazy, como o mapa. */}
      <Route
        path="/provisoes"
        element={(
          <Protegida permitido={podeVerFinancas(papel)}>
            <Suspense fallback={<Carregando texto="Abrindo as provisões" />}>
              <Provisoes />
            </Suspense>
          </Protegida>
        )}
      />

      {/* Parceiros (062): os pedidos do portal B2B + gestão dos logins.
          Gerência — o vendedor LÊ os pedidos pela RLS, mas quem confirma,
          cancela e cria acesso é gerente/dono. */}
      {/* 28/09/2026, segunda leitura: a rota VOLTOU para a gestão de vendas.
          Ela tinha virado de dono porque a lista de PEDIDOS carrega o total —
          e isso levou junto duas coisas que não são faturamento: criar o LOGIN
          do parceiro e definir o DESCONTO dele. As duas seguem concedidas ao
          gerente pela própria RLS (§17: select e update(desconto_pct) em
          partner_accounts continuam 'gerente','dono'), então fechar a rota
          tirava dele um trabalho que o banco lhe dá. A trava é POR ABA, dentro
          da tela: pedidos e resumo por cliente só com podeVerFaturamento. */}
      <Route path="/parceiros" element={<Protegida permitido={podeGerenciarVendas(papel)}><Parceiros /></Protegida>} />

      {/* Compras (040): pedido ao FORNECEDOR — gerência, a mesma régua da
          entrada de mercadoria. Não confundir com /encomendas, que é a peça
          separada para um CLIENTE. */}
      <Route path="/compras" element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Compras /></Protegida>} />

      {/* Fiado (042): quem está devendo. Gerência — o vendedor VENDE fiado,
          mas quem cobra é quem gerencia (a mesma régua das vendas). */}
      <Route path="/fiado" element={<Protegida permitido={podeGerenciarVendas(papel)}><Fiado /></Protegida>} />

      {/* Orçamento é papel de balcão, não dinheiro: todo perfil ativo emite
          (pela tela de Venda), consulta e converte (rls.sql §10). */}
      <Route path="/orcamentos" element={<Protegida permitido><Orcamentos /></Protegida>} />
      <Route path="/orcamentos/:id" element={<Protegida permitido><Orcamento /></Protegida>} />

      {/* Mapa da loja em 3D (25/09/2026): todo perfil ativo consulta — achar
          a peça é trabalho de balcão. Mudar a zona de uma peça é update de
          stock, que a RLS só aceita de gerente/dono (rls.sql §1). */}
      <Route
        path="/mapa"
        element={(
          <Protegida permitido>
            <Suspense fallback={<Carregando texto="Abrindo o mapa" />}>
              <MapaDaLoja />
            </Suspense>
          </Protegida>
        )}
      />

      {/* Metas do trimestre: só o dono, desde 28/09/2026. Meta é faturamento
          com nome de alvo — era a tela em que a equipe inteira via quanto a
          loja vende, e foi exatamente isso que o dono mandou tirar. Ninguém
          perdeu dado: não havia nenhuma meta cadastrada no banco. */}
      <Route path="/metas" element={<Protegida permitido={podeVerFaturamento(papel)}><Metas /></Protegida>} />

      {/* O cadastro COMPLETO (custo, margem, similares, fornecedor) é da
          gerência. O balcão cadastra pela tela própria, /cadastro/balcao, que
          grava pela RPC cadastrar_peca_pelo_balcao: a peça do vendedor nasce
          aguardando liberação e não vende até o gerente ou o dono liberar
          (fila de conferência, 093). A porta da tela do vendedor é o vazio da
          busca (BuscaPecas). O insert direto em products pela API ainda está
          aberto a todo perfil (§1.1 do rls.sql) — fechá-lo é o PR seguinte. */}
      <Route
        path="/cadastro"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Cadastro /></Protegida>}
      />
      <Route
        path="/cadastro/balcao"
        element={<Protegida permitido={podeCadastroRapido(papel)}><CadastroDoBalcao /></Protegida>}
      />
      {/* A fila de conferência: a gerência libera e recusa; o vendedor vê as
          peças que ele cadastrou, com a rasura e o motivo. Uma rota, duas
          telas — o recorte é por papel lá dentro e pela RLS da papelada. */}
      <Route path="/conferencia" element={<Protegida permitido><Conferencia /></Protegida>} />
      {/* A lista de faxina (120 — 18/10/2026): o que o balcão vendeu,
          encomendou ou orçou sem ter no catálogo, o mais pedido primeiro. É o
          INVERSO da fila de conferência (que é peça já cadastrada esperando
          liberação). Gerente e dono: a tela não tem dinheiro nenhum, mas lê a
          venda, a encomenda e o orçamento de todo mundo — e quem recorta isso
          é o exige_papel no corpo das RPCs, não esta linha. */}
      <Route
        path="/faxina"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Faxina /></Protegida>}
      />
      <Route
        path="/peca/:id/editar"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Cadastro /></Protegida>}
      />
      <Route
        path="/importar"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Importar /></Protegida>}
      />
      <Route
        path="/entrada"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Entrada /></Protegida>}
      />
      {/* "Paguei fornecedor" (095): a compra paga na hora, em 10 segundos.
          Gerente e dono LANÇAM; o mês (lista e total) é só do dono, e a trava
          está dentro da tela e na RLS de contas_pagar. Quem nega a escrita é
          o exige_papel de registrar_compra_na_hora. */}
      <Route
        path="/compra-na-hora"
        element={<Protegida permitido={podeLancarCompraNaHora(papel)}><CompraNaHora /></Protegida>}
      />

      {/* O caixa é só do dono desde 28/09/2026 (podeVerCaixa estreitou): contar
          o dinheiro do dia é ver o faturamento do dia. */}
      <Route path="/caixa" element={<Protegida permitido={podeVerCaixa(papel)}><Caixa /></Protegida>} />
      {/* Relatórios é UMA tela com dois assuntos, e a rota é do gerente por
          causa do segundo: a LISTA DE REPOSIÇÃO — o que saiu e precisa ser
          comprado, em unidades. Se esta rota fosse só do dono (28/09/2026), o
          gerente perderia a reposição junto com o faturamento; então a trava
          de dinheiro é POR BLOCO, dentro da tela (podeVerFaturamento lá). */}
      <Route
        path="/relatorios"
        element={<Protegida permitido={podeGerenciarCatalogo(papel)}><Relatorios /></Protegida>}
      />

      {/* Emitir vale é dar crédito da loja: gerência (a mesma régua do fiado
          desde 28/09/2026 — antes era a do caixa). O RESGATE acontece na tela
          de Venda e está aberto a todo vendedor. */}
      <Route path="/a-ver" element={<Protegida permitido={podeGerenciarVendas(papel)}><AVer /></Protegida>} />

      {/* Dinheiro de pessoa e resultado do negócio: só o dono. */}
      <Route path="/painel" element={<Protegida permitido={podeVerFinancas(papel)}><Painel /></Protegida>} />
      {/* Gerente entra: cadastra vendedor. Dono faz tudo. A diferença é
          aplicada pela RLS e pela Edge Function, não por esta linha. */}
      <Route path="/equipe" element={<Protegida permitido={podeGerenciarEquipe(papel)}><Equipe /></Protegida>} />

      {/* Só dono. Características mudam a FORMA do catálogo e estoque é o
          patrimônio da loja — as duas telas espelham a RLS, que é quem nega
          de verdade (rls.sql §12 e a RPC ajustar_estoque). */}
      <Route
        path="/caracteristicas"
        element={<Protegida permitido={podeAdministrar(papel)}><Caracteristicas /></Protegida>}
      />
      <Route
        path="/estoque"
        element={<Protegida permitido={podeAdministrar(papel)}><Estoque /></Protegida>}
      />
      {/* Sistema: saúde da VPS, backup, erros e o botão de publicar. É a tela
          que substitui o SSH — e por isso é só do dono, como o agente exige do
          outro lado (ele revalida o papel no banco antes de qualquer coisa). */}
      <Route
        path="/sistema"
        element={<Protegida permitido={podeAdministrar(papel)}><Sistema /></Protegida>}
      />
      {/* Meu dinheiro (ADR-008): o planejamento de cada pessoa, num banco à
          parte que ninguém da loja lê — nem o dono. Aparece para o papel que
          o dono liberou na tela de Equipe. */}
      <Route path="/pessoal/meu-dinheiro" element={<RotaDoMeuDinheiro />} />

      {/* /admin era a tela antiga de colar UUID à mão — virou /equipe. */}
      <Route path="/admin" element={<Navigate to="/equipe" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
